import { Text } from '@pm/ui';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

const detailList = cva('rounded-lg px-m py-xs', {
  variants: {
    tone: {
      neutral: 'bg-surface-strong',
      accent: 'bg-accent-medium',
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
});

export type DetailListRow = {
  label: string;
  value: string;
  /** Renders the value as an anchor, e.g. `tel:` for a phone number. */
  href?: string;
  /**
   * Puts the value on its own line below the label instead of opposite it.
   * Use with longer text
   */
  stacked?: boolean;
};

type SkippedRow = false | '' | 0 | null | undefined;

type DetailListProps = {
  rows: (DetailListRow | SkippedRow)[];
  tone?: 'neutral' | 'accent';
};

export const DetailList = ({ rows, tone }: DetailListProps) => {
  const visibleRows = rows.filter((row): row is DetailListRow => Boolean(row));
  const isAccent = tone === 'accent';
  const labelColor = isAccent ? 'contentInverse' : 'contentSubdued';
  const valueColor = isAccent ? 'contentInverse' : 'contentDefault';

  if (visibleRows.length === 0) {
    return null;
  }

  return (
    <dl className={detailList({ tone })}>
      {visibleRows.map((row, index) => (
        <div
          key={row.label}
          className={clsx('py-xs', {
            'flex items-center justify-between gap-m': !row.stacked,
            'space-y-xxs': row.stacked,
            'border-b border-default': index < visibleRows.length - 1,
          })}
        >
          <Text as="dt" size="bodyS" color={labelColor}>
            {row.label}
          </Text>
          <Text
            as="dd"
            size="bodyS"
            color={valueColor}
            className={row.stacked ? undefined : 'text-right'}
          >
            {row.href ? (
              <a
                href={row.href}
                className={clsx(
                  'underline',
                  isAccent ? 'text-accent-subtle-hover' : 'text-content-link',
                )}
              >
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </Text>
        </div>
      ))}
    </dl>
  );
};
